import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

const SIGNED_URL_TTL_SECONDS = 3600;

/**
 * Generate a short-lived signed URL for a trailer image stored in the
 * private `trailer-images` bucket. Returns null if no path is provided
 * or if signing fails.
 */
export async function getTrailerImageSignedUrl(
  imagePath: string | null | undefined,
): Promise<string | null> {
  if (!imagePath) return null;
  const { data, error } = await supabase.storage
    .from("trailer-images")
    .createSignedUrl(imagePath, SIGNED_URL_TTL_SECONDS);
  if (error) {
    console.error("trailer-images signed URL error:", error);
    return null;
  }
  return data?.signedUrl ?? null;
}

/**
 * React hook: resolves a single trailer image path into a signed URL.
 */
export function useTrailerImageUrl(imagePath: string | null | undefined): string | null {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!imagePath) {
      setUrl(null);
      return;
    }
    getTrailerImageSignedUrl(imagePath).then((signed) => {
      if (!cancelled) setUrl(signed);
    });
    return () => {
      cancelled = true;
    };
  }, [imagePath]);

  return url;
}

/**
 * React hook: batch-resolves many trailer image paths into a
 * `{ [imagePath]: signedUrl }` map. Skips null/empty paths.
 */
export function useTrailerImageUrls(
  imagePaths: Array<string | null | undefined>,
): Record<string, string> {
  const unique = Array.from(
    new Set(imagePaths.filter((p): p is string => !!p)),
  );
  const key = unique.sort().join("|");
  const [urls, setUrls] = useState<Record<string, string>>({});

  useEffect(() => {
    let cancelled = false;
    if (unique.length === 0) {
      setUrls({});
      return;
    }
    Promise.all(
      unique.map(async (path) => {
        const signed = await getTrailerImageSignedUrl(path);
        return [path, signed] as const;
      }),
    ).then((entries) => {
      if (cancelled) return;
      const next: Record<string, string> = {};
      for (const [path, signed] of entries) {
        if (signed) next[path] = signed;
      }
      setUrls(next);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return urls;
}